import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { ApiError, apiGet } from './api';

export interface AdminMe {
  user_id: string;
  email: string;
  role: string;
}

export function useSession() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, loading };
}

// A 401/403 here is a definitive answer, not a transient failure — retrying it
// just delays the screen the caller needs to see. A 429 (the per-admin rate
// limit) is the same: retrying delays a screen that already knows what's wrong.
// Only a transient server failure (5xx) — or no response at all, e.g. a raw
// network error from a dropped connection — gets retried, twice. `apiGet`
// (lib/api.ts) only wraps a non-2xx HTTP response as an ApiError; a `fetch()`
// that never gets a response throws a bare Error with no `.status`, which is
// why `error` here is typed narrower than what can actually reach this
// function at runtime — react-query does not itself guarantee the error type
// matches the query's declared error generic. Mirrors the QueryClient default
// in main.tsx exactly (undefined status retries the same as 5xx does) so a
// caller doesn't have to know which retry policy is in effect for a given
// query — a network blip fails the same way everywhere in this app.
export function shouldRetryAdminMe(failureCount: number, error: ApiError): boolean {
  const status = (error as { status?: number }).status;
  return (status === undefined || status >= 500) && failureCount < 2;
}

export function useAdminMe(enabled: boolean) {
  return useQuery<AdminMe, ApiError>({
    queryKey: ['admin', 'me'],
    enabled,
    retry: shouldRetryAdminMe,
    queryFn: () => apiGet('/admin/me') as Promise<AdminMe>,
  });
}
